import { Sides } from "@project/emerge-types";

/** Matches manufacturer default in createCaseInterior */
const DEFAULT_TOLERANCE = { x: 7, y: 2, z: 7 } as const;

type PaddingMap = Record<number, number>;
type Vec3 = { x: number; y: number; z: number };

type CompatItem = {
  width: number;
  height: number;
  depth: number;
  padding: PaddingMap;
  paddingExtraForBottom?: number;
};

type CompatNode = {
  type?: number;
  orientation?: number;
  item?: CompatItem;
  children?: CompatNode[];
};

type Tolerance = { x: number; y: number; z: number };

type AxisPermutation = readonly [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2];

const ORIENTATION_NORMAL = 0;
const NODE_TYPE_ITEM = 1;

const DEFAULT_AXIS_PERMUTATION: AxisPermutation = [0, 1, 2];

const baseChangeMap: Record<number, AxisPermutation> = {
  0: [0, 1, 2],
  1: [2, 1, 0],
  2: [1, 0, 2],
  3: [0, 2, 1],
  4: [1, 2, 0],
  5: [2, 0, 1],
};

function getBaseChangeMap(orientation: number): AxisPermutation {
  return baseChangeMap[orientation] ?? DEFAULT_AXIS_PERMUTATION;
}

/** Pre-fix #179: bare permutation without axis-sign correction */
function legacyReorient(orientation: number, side: Sides): Sides {
  const base = Math.floor(side / 2);
  const variant = side % 2;
  return ((getBaseChangeMap(orientation)[base] ?? 0) * 2 + variant) as Sides;
}

const SIDE_GROUP_DIM_SWAP = [0, 2, 1] as const;
type AxisSigns = readonly [1 | -1, 1 | -1, 1 | -1];
const axisSigns: Record<number, AxisSigns> = {
  0: [1, 1, 1],
  1: [1, 1, -1],
  2: [1, -1, 1],
  3: [1, 1, -1],
  4: [1, 1, 1],
  5: [1, 1, 1],
};

/** Fixed #179: accounts for axis signs when mapping faces */
function newReorient(orientation: number, side: Sides): Sides {
  const worldDim = SIDE_GROUP_DIM_SWAP[Math.floor(side / 2)] ?? 0;
  const itemDim = getBaseChangeMap(orientation)[worldDim] ?? 0;
  const worldDir = side % 2 === 1 ? 1 : -1;
  const itemDir = (axisSigns[orientation]?.[worldDim] ?? 1) * worldDir;
  const sideGroup = SIDE_GROUP_DIM_SWAP[itemDim] ?? 0;
  return (sideGroup * 2 + (itemDir > 0 ? 1 : 0)) as Sides;
}

function reorientPadding(
  orientation: number,
  padding: PaddingMap,
  reorientFn: (orientation: number, side: Sides) => Sides,
): PaddingMap {
  return {
    [Sides.BACK]: padding[reorientFn(orientation, Sides.BACK)] ?? 0,
    [Sides.FRONT]: padding[reorientFn(orientation, Sides.FRONT)] ?? 0,
    [Sides.LEFT]: padding[reorientFn(orientation, Sides.LEFT)] ?? 0,
    [Sides.RIGHT]: padding[reorientFn(orientation, Sides.RIGHT)] ?? 0,
    [Sides.TOP]: padding[reorientFn(orientation, Sides.TOP)] ?? 0,
    [Sides.BOTTOM]: padding[reorientFn(orientation, Sides.BOTTOM)] ?? 0,
  };
}

function normalizePadding(raw: unknown): PaddingMap {
  if (!raw || typeof raw !== "object") return {};
  const map: PaddingMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      map[Number(key)] = value;
    }
  }
  return map;
}

function calculateNodeInner(orientation: number, item: CompatItem): Vec3 {
  const keys = ["width", "height", "depth"] as const;
  const map = getBaseChangeMap(orientation);
  return {
    x: item[keys[map[0]]],
    y: item[keys[map[1]]],
    z: item[keys[map[2]]],
  };
}

export function calculateLegacyNodeOuter(
  orientation: number,
  item: CompatItem,
  tolerance: Tolerance = DEFAULT_TOLERANCE,
): Vec3 {
  return calculateNodeOuter(orientation, item, tolerance, true);
}

export function calculateNewNodeOuter(
  orientation: number,
  item: CompatItem,
  tolerance: Tolerance = DEFAULT_TOLERANCE,
): Vec3 {
  return calculateNodeOuter(orientation, item, tolerance, false);
}

function calculateNodeOuter(
  orientation: number,
  item: CompatItem,
  tolerance: Tolerance,
  useLegacyReorient: boolean,
): Vec3 {
  const inner = calculateNodeInner(orientation, item);
  const reorientFn = useLegacyReorient ? legacyReorient : newReorient;
  const padding = reorientPadding(orientation, item.padding, reorientFn);
  return {
    x:
      (padding[Sides.LEFT] ?? 0) +
      (padding[Sides.RIGHT] ?? 0) +
      tolerance.x +
      inner.x,
    y:
      Math.max(padding[Sides.BOTTOM] ?? 0, item.paddingExtraForBottom ?? 0) +
      tolerance.y +
      inner.y,
    z:
      (padding[Sides.FRONT] ?? 0) +
      (padding[Sides.BACK] ?? 0) +
      tolerance.z +
      inner.z,
  };
}

const ALL_SIDES = [
  Sides.LEFT,
  Sides.RIGHT,
  Sides.BACK,
  Sides.FRONT,
  Sides.BOTTOM,
  Sides.TOP,
] as const;

function isUniformPadding(padding: PaddingMap): boolean {
  const first = padding[Sides.LEFT] ?? 0;
  return ALL_SIDES.every((side) => (padding[side] ?? 0) === first);
}

/** Rewrite stored item-local padding so fixed reorient yields legacy world padding. */
function convertPaddingLegacyToNew(
  orientation: number,
  padding: PaddingMap,
): PaddingMap {
  const result: PaddingMap = {};
  for (const worldSide of ALL_SIDES) {
    const legacyItemSide = legacyReorient(orientation, worldSide);
    const newItemSide = newReorient(orientation, worldSide);
    result[newItemSide] = padding[legacyItemSide] ?? 0;
  }
  return result;
}

function migrateItemNode(node: CompatNode): void {
  const item = node.item;
  if (!item) return;

  const orientation = node.orientation ?? ORIENTATION_NORMAL;
  if (orientation === ORIENTATION_NORMAL) return;

  const padding = normalizePadding(item.padding);
  if (isUniformPadding(padding)) return;

  item.padding = convertPaddingLegacyToNew(orientation, padding);
}

function walkTree(node: CompatNode): void {
  if (node.type === NODE_TYPE_ITEM) {
    migrateItemNode(node);
    return;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child && typeof child === "object") {
        walkTree(child);
      }
    }
  }
}

/**
 * Rewrites the stored item-local padding of turned compartments (#179) so
 * that the fixed `reorient` places every padding value on the same world side
 * as the pre-fix code did. A rebuild of a legacy case thus keeps its outer
 * dimensions and parts. Only ITEM nodes with non-uniform padding and an
 * orientation other than NORMAL change.
 *
 * Not idempotent, and it cannot tell legacy from current data: apply it once,
 * and only to settings known to come from the pre-fix code (the huge-batch
 * golden rebuild and the debug case page). Never call it from
 * `migrateFlightCaseSettings`, which also runs on current data.
 */
export function migrateLegacyReorientPaddingCompat(
  settings: Record<string, unknown>,
): void {
  const interior = settings.interiorOptions;
  if (!interior || typeof interior !== "object") return;

  const tree = (interior as Record<string, unknown>).tree;
  if (!tree || typeof tree !== "object") return;

  walkTree(tree as CompatNode);
}
